/**
 * Cobo 跨链充值订单：状态语义与账号充值日志一致。
 * 后端约定 status 为数字：1–4 进行中 · 5 已入账（唯一成功）· 99 异常。
 * 此前 UI 保持转圈，仅 status===5 标成功。
 */

export const COBO_ORDER_STATUS = {
  CONFIRMING: 1,
  TRANSFERRING_TO_WALLET: 2,
  EXCHANGE_DONE: 3,
  PAYING_OUT: 4,
  COMPLETED: 5,
  FAILED: 99,
};

/**
 * 主路径：接口回数字 1–5 / 99（或字符串 "1"…"5"）。
 * 下列别名仅兜底历史/异常字段（statusText、coboTxId 后缀等），勿当主契约。
 */
const COBO_ORDER_STATUS_ALIASES = {
  ...COBO_ORDER_STATUS,
  PENDING: COBO_ORDER_STATUS.CONFIRMING,
  PROCESSING: COBO_ORDER_STATUS.CONFIRMING,
  CONFIRM: COBO_ORDER_STATUS.CONFIRMING,
  TRANSFERRING: COBO_ORDER_STATUS.TRANSFERRING_TO_WALLET,
  EXCHANGING: COBO_ORDER_STATUS.EXCHANGE_DONE,
  PAYOUT: COBO_ORDER_STATUS.PAYING_OUT,
  PAYING: COBO_ORDER_STATUS.PAYING_OUT,
  SUCCESS: COBO_ORDER_STATUS.COMPLETED,
  SUCCEEDED: COBO_ORDER_STATUS.COMPLETED,
  DONE: COBO_ORDER_STATUS.COMPLETED,
  CREDITED: COBO_ORDER_STATUS.COMPLETED,
  SETTLED: COBO_ORDER_STATUS.COMPLETED,
  FAIL: COBO_ORDER_STATUS.FAILED,
  ERROR: COBO_ORDER_STATUS.FAILED,
  EXCEPTION: COBO_ORDER_STATUS.FAILED,
};

/** 兑换状态（与订单流水 status 不同）：0/1 进行中 · 2 成功 · 3 失败 */
export const COBO_EXCHANGE_STATUS = {
  PENDING: 0,
  PROCESSING: 1,
  SUCCESS: 2,
  FAILED: 3,
};

export const COBO_ORDER_POLL_MS = 3_000;

/** 距当前超过此时长的订单一律不弹充值状态提示（与本地 seen 无关） */
export const COBO_ORDER_PROMPT_MAX_AGE_MS = 5 * 60 * 1000;

export function normalizeCoboOrdersPage(data) {
  const rows = Array.isArray(data?.rows)
    ? data.rows
    : Array.isArray(data?.data?.rows)
    ? data.data.rows
    : Array.isArray(data)
    ? data
    : [];
  const total = Number(data?.total ?? data?.data?.total ?? rows.length) || 0;
  return { rows, total };
}

export function parseCoboOrderStatus(status) {
  if (status == null || status === '') return NaN;
  const n = Number(status);
  if (Number.isFinite(n)) return n;
  // 兼容异常回枚举名（主路径已是数字 1–5）
  const name = String(status)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (!name) return NaN;
  if (Object.prototype.hasOwnProperty.call(COBO_ORDER_STATUS_ALIASES, name)) {
    return COBO_ORDER_STATUS_ALIASES[name];
  }
  return NaN;
}

/**
 * 从整单解析状态：优先数字 status，其次 statusText / coboTxId 后缀兜底
 */
export function resolveCoboOrderStatus(order) {
  const fromStatus = parseCoboOrderStatus(order?.status);
  if (Number.isFinite(fromStatus)) return fromStatus;
  const fromText = parseCoboOrderStatus(order?.statusText);
  if (Number.isFinite(fromText)) return fromText;
  const coboTxId = String(order?.coboTxId ?? order?.cobo_tx_id ?? '').trim();
  const suffix = coboTxId.match(/:([A-Za-z0-9_]+)$/);
  if (suffix?.[1]) {
    const fromSuffix = parseCoboOrderStatus(suffix[1]);
    if (Number.isFinite(fromSuffix)) return fromSuffix;
  }
  return NaN;
}

export function isCoboOrderCompleted(status) {
  return parseCoboOrderStatus(status) === COBO_ORDER_STATUS.COMPLETED;
}

export function isCoboOrderFailed(status) {
  return parseCoboOrderStatus(status) === COBO_ORDER_STATUS.FAILED;
}

export function isCoboOrderInProgress(status) {
  const n = parseCoboOrderStatus(status);
  if (!Number.isFinite(n)) return false;
  return n >= 1 && n <= 4;
}

export function isCoboOrderCompletedOrder(order) {
  return resolveCoboOrderStatus(order) === COBO_ORDER_STATUS.COMPLETED;
}

export function isCoboOrderFailedOrder(order) {
  return resolveCoboOrderStatus(order) === COBO_ORDER_STATUS.FAILED;
}

export function isCoboOrderInProgressOrder(order) {
  const n = resolveCoboOrderStatus(order);
  if (!Number.isFinite(n)) return false;
  return n >= 1 && n <= 4;
}

/** i18n key：account.rechargeLog.status.{n} */
export function coboOrderStatusI18nKey(status) {
  const n = parseCoboOrderStatus(status);
  if (!Number.isFinite(n)) return '';
  return `account.rechargeLog.status.${n}`;
}

export function parseCoboExchangeStatus(status) {
  if (status == null || status === '') return NaN;
  const n = Number(status);
  if (Number.isFinite(n)) return n;
  const name = String(status)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  if (!name) return NaN;
  const aliases = {
    ...COBO_EXCHANGE_STATUS,
    DEPOSITING: 1,
    IN_PROGRESS: 1,
    RUNNING: 1,
    SUCCEEDED: 2,
    COMPLETED: 2,
    DONE: 2,
    FAIL: 3,
    ERROR: 3,
    EXCEPTION: 3,
  };
  return Object.prototype.hasOwnProperty.call(aliases, name) ? aliases[name] : NaN;
}

/** i18n key：account.rechargeLog.exchangeStatus.{n}（勿复用订单 status 解析） */
export function coboExchangeStatusI18nKey(status) {
  const n = parseCoboExchangeStatus(status);
  if (!Number.isFinite(n)) return '';
  return `account.rechargeLog.exchangeStatus.${n}`;
}

/** 非 EVM（Solana base58 / Tron 等）：排除纯数字订单号后保留原文 */
export function looksLikeChainTxId(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  if (/^\d{10,}$/.test(raw)) return false;
  if (looksLikeTxHash(raw)) return true;
  return raw.length >= 32;
}

export function extractCoboDepositTxid(order) {
  const candidates = [
    order?.depositTxid,
    order?.depositTxId,
    order?.txHash,
    order?.txid,
    order?.txId,
  ];
  for (const item of candidates) {
    const value = String(item ?? '').trim();
    if (looksLikeTxHash(value)) return normalizeTxHashKey(value);
    if (looksLikeChainTxId(value)) return value;
  }
  return '';
}

/** 统一 tx 比较键：补齐 0x、小写 */
export function normalizeTxHashKey(value) {
  const raw = String(value ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return '';
  if (/^[a-f0-9]{64}$/.test(raw)) return `0x${raw}`;
  return raw;
}

/** 仅识别真实链上 hash，排除 coboTxId 这类时间戳订单号（如 20260715…） */
export function looksLikeTxHash(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  if (/^\d{10,}$/.test(raw)) return false;
  if (/^0x[a-fA-F0-9]{64}$/i.test(raw)) return true;
  if (/^[a-fA-F0-9]{64}$/i.test(raw)) return true;
  return false;
}

export function extractCoboCompletionTxid(order) {
  const candidates = [
    order?.withdrawTxid,
    order?.withdrawTxId,
    order?.completionTxid,
    order?.completionTxId,
    order?.toTxid,
    order?.toTxId,
    order?.privyTxid,
    order?.privyTxId,
  ];
  for (const item of candidates) {
    const value = String(item ?? '').trim();
    if (looksLikeTxHash(value)) return normalizeTxHashKey(value);
  }
  return '';
}

export function extractCoboOrderId(order) {
  return String(order?.id ?? order?.orderId ?? order?.coboTxId ?? '').trim();
}

export function extractCoboPrivyAddress(order) {
  return String(order?.privyWalletAddress ?? order?.privyAddress ?? '').trim();
}

export function extractCoboDepositChain(order) {
  return String(order?.depositChain ?? order?.chain ?? '')
    .trim()
    .toUpperCase();
}

/** 入金链浏览器根 URL（用于充值 tx 外链；TRON 路径特殊） */
export function resolveCoboDepositExplorerUrl(depositChain) {
  const key = String(depositChain ?? '')
    .trim()
    .toUpperCase();
  const map = {
    ETH: 'https://etherscan.io',
    ETHEREUM: 'https://etherscan.io',
    POLYGON: 'https://polygonscan.com',
    MATIC: 'https://polygonscan.com',
    ARBITRUM: 'https://arbiscan.io',
    BSC: 'https://bscscan.com',
    BASE: 'https://basescan.org',
    SOLANA: 'https://solscan.io',
    // DepositStatusCard 拼 /tx/；tronscan 用 /#/transaction/ 需完整前缀
    TRON: 'https://tronscan.org/#/transaction',
  };
  return map[key] || '';
}

/** Cobo 确认完成时间（非 Privy 到账） */
export function extractCoboDepositConfirmedAtMs(order) {
  return (
    coerceTimeMs(order?.coboDepositConfirmedAt) || coerceTimeMs(order?.depositConfirmedAt) || 0
  );
}

/** Privy 到账时间：优先 updatedAt（status=5） */
export function extractCoboCompletedAtMs(order) {
  return (
    coerceTimeMs(order?.updatedAt) ||
    coerceTimeMs(order?.updateAt) ||
    coerceTimeMs(order?.completedAt) ||
    0
  );
}

/**
 * 展示金额：status=5 优先 withdrawAmount（Privy 实到）；此前用 depositAmount。
 */
export function extractCoboDisplayAmount(order) {
  const status = resolveCoboOrderStatus(order);
  if (isCoboOrderCompleted(status)) {
    const withdraw = Number(order?.withdrawAmount);
    if (Number.isFinite(withdraw) && withdraw > 0) return withdraw;
  }
  const deposit = Number(order?.depositAmount ?? order?.amount);
  return Number.isFinite(deposit) && deposit > 0 ? deposit : undefined;
}

export function addressesEqual(a, b) {
  const left = String(a ?? '')
    .trim()
    .toLowerCase();
  const right = String(b ?? '')
    .trim()
    .toLowerCase();
  if (!left || !right) return false;
  return left === right;
}

/** 多链：归一化收款地址列表（小写去重） */
export function normalizeCoboAddressList(input) {
  const raw = Array.isArray(input) ? input : input ? [input] : [];
  return [
    ...new Set(
      raw
        .map((item) =>
          String(item ?? '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    ),
  ];
}

/** 订单收款地址是否落在允许的多链地址集合内 */
export function coboAddressAllowed(orderOrAddress, allowedAddresses) {
  const allowed = normalizeCoboAddressList(allowedAddresses);
  if (!allowed.length) return false;
  const addr =
    typeof orderOrAddress === 'string' || orderOrAddress == null
      ? String(orderOrAddress ?? '').trim()
      : extractCoboDepositAddress(orderOrAddress);
  const key = addr.toLowerCase();
  if (!key) return false;
  return allowed.includes(key);
}

/** 订单收款地址（字段名因后端版本可能不同） */
export function extractCoboDepositAddress(order) {
  return String(
    order?.depositAddress ??
      order?.toAddress ??
      order?.address ??
      order?.receiveAddress ??
      order?.coboAddress ??
      ''
  ).trim();
}

/** 统一充值币种展示：USDC.e / usd-coin → USDC */
export function normalizeDepositTokenSymbol(value) {
  const raw = String(value ?? '')
    .trim()
    .toUpperCase();
  if (!raw) return '';
  if (raw === 'USDC' || raw === 'USDC.E' || raw === 'USDCE' || raw === 'USD-COIN' || raw === 'U') {
    return 'USDC';
  }
  if (raw === 'USDT' || raw === 'USDT.E' || raw === 'USDTE') return 'USDT';
  return raw;
}

export function extractCoboDepositCurrency(order) {
  return normalizeDepositTokenSymbol(
    order?.depositCurrency ??
      order?.currency ??
      order?.tokenSymbol ??
      order?.token ??
      order?.asset ??
      order?.coin ??
      ''
  );
}

function coerceTimeMs(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 0 && raw < 1e12 ? Math.round(raw * 1000) : Math.round(raw);
  }
  const text = String(raw).trim();
  if (!text) return 0;
  if (/^\d+$/.test(text)) {
    const n = Number(text);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n < 1e12 ? Math.round(n * 1000) : Math.round(n);
  }
  return Date.parse(text) || 0;
}

export function orderCreatedMs(order) {
  return (
    coerceTimeMs(order?.createdAt) ||
    coerceTimeMs(order?.createAt) ||
    coerceTimeMs(order?.createTime) ||
    coerceTimeMs(order?.createdTime) ||
    coerceTimeMs(order?.updatedAt) ||
    coerceTimeMs(order?.updateAt) ||
    0
  );
}

/** 提示用时间窗：max(会话起点, 当前−5min) */
export function coboOrderPromptNotBeforeMs(sessionFloorMs = 0, now = Date.now()) {
  const ageFloor = Number(now) - COBO_ORDER_PROMPT_MAX_AGE_MS;
  return Math.max(ageFloor, Math.max(0, Number(sessionFloorMs) || 0));
}

/** 是否仍在「可提示」年龄内；无创建时间视为过旧，不提示 */
export function isCoboOrderFreshForPrompt(order, now = Date.now()) {
  const created = orderCreatedMs(order);
  if (!created) return false;
  return created >= Number(now) - COBO_ORDER_PROMPT_MAX_AGE_MS;
}

/**
 * 是否应对该单弹充值状态提示：
 * 1) 创建超过 5 分钟 → 否
 * 2) 5 分钟内但已提示过（seen）→ 否
 * 3) 否则 → 是
 */
export function shouldPromptCoboOrder(order, seenOrderIds = [], now = Date.now()) {
  if (!isCoboOrderFreshForPrompt(order, now)) return false;
  const id = extractCoboOrderId(order);
  if (id) {
    const seen = new Set(
      (Array.isArray(seenOrderIds) ? seenOrderIds : [])
        .map((item) => String(item ?? '').trim())
        .filter(Boolean)
    );
    if (seen.has(id)) return false;
  }
  return true;
}

/** 同地址（或多链任一收款地址）会话内进行中订单里最新的一笔 */
export function pickNewestInProgressCoboOrder(orders, coboAddressOrList, { notBeforeMs = 0 } = {}) {
  const list = Array.isArray(orders) ? orders : [];
  const floor = Number(notBeforeMs) > 0 ? Number(notBeforeMs) : 0;
  const allowed = normalizeCoboAddressList(coboAddressOrList);

  function inSession(row) {
    if (!isCoboOrderInProgressOrder(row)) return false;
    if (floor > 0) {
      const created = orderCreatedMs(row);
      if (created && created < floor) return false;
    }
    return true;
  }

  let candidates = list.filter((row) => coboAddressAllowed(row, allowed) && inSession(row));
  // 列表接口按用户隔离；若后端未返回收款地址字段，不能整段丢弃中间态
  if (!candidates.length) {
    const hasAnyAddress = list.some((row) => extractCoboDepositAddress(row));
    if (!hasAnyAddress) {
      candidates = list.filter((row) => inSession(row));
    }
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => {
    const dt = orderCreatedMs(b) - orderCreatedMs(a);
    if (dt !== 0) return dt;
    return String(extractCoboOrderId(b)).localeCompare(String(extractCoboOrderId(a)));
  });
  return candidates[0];
}

/**
 * 将订单匹配到本地充值条目：优先 depositTxid，其次 orderId。
 * 地址兜底仅用于「本会话开始后」的新单，避免挂历史未完成单。
 */
export function findCoboOrderForItem(orders, item, { notBeforeMs = 0 } = {}) {
  const list = Array.isArray(orders) ? orders : [];
  if (!item || !list.length) return null;

  const tx = normalizeTxHashKey(item.txHash);
  if (tx) {
    const byTx = list.find((row) => normalizeTxHashKey(extractCoboDepositTxid(row)) === tx);
    if (byTx) return byTx;
  }

  const orderId = String(item.coboOrderId ?? '').trim();
  if (orderId) {
    const byId = list.find((row) => extractCoboOrderId(row) === orderId);
    if (byId) return byId;
  }

  const coboAddr = item.coboAddress || item.watchAddress;
  const itemSymbol = normalizeDepositTokenSymbol(item.tokenSymbol);
  const floor = Number(notBeforeMs) > 0 ? Number(notBeforeMs) : 0;
  if (!floor) return null;

  let candidates = list.filter((row) => {
    if (!addressesEqual(extractCoboDepositAddress(row), coboAddr)) return false;
    if (!isCoboOrderInProgressOrder(row)) return false;
    const created = orderCreatedMs(row);
    if (created && created < floor) return false;
    const cur = extractCoboDepositCurrency(row);
    return !itemSymbol || !cur || cur === itemSymbol;
  });
  if (!candidates.length) {
    const hasAnyAddress = list.some((row) => extractCoboDepositAddress(row));
    if (!hasAnyAddress) {
      candidates = list.filter((row) => {
        if (!isCoboOrderInProgressOrder(row)) return false;
        const created = orderCreatedMs(row);
        if (created && created < floor) return false;
        const cur = extractCoboDepositCurrency(row);
        return !itemSymbol || !cur || cur === itemSymbol;
      });
    }
  }
  if (!candidates.length) return null;

  candidates.sort((a, b) => orderCreatedMs(b) - orderCreatedMs(a));
  return candidates[0];
}

/**
 * 本会话新建、尚未纳入本地队列的进行中订单（不包含扫码前的历史未到账单）。
 */
export function findUntrackedCoboOrders(orders, items, coboAddress, { notBeforeMs = 0 } = {}) {
  const list = Array.isArray(orders) ? orders : [];
  const existing = Array.isArray(items) ? items : [];
  const knownTx = new Set(existing.map((item) => normalizeTxHashKey(item.txHash)).filter(Boolean));
  const knownIds = new Set(
    existing.map((item) => String(item.coboOrderId ?? '').trim()).filter(Boolean)
  );
  const floor = Number(notBeforeMs) > 0 ? Number(notBeforeMs) : 0;

  return list.filter((row) => {
    if (!addressesEqual(extractCoboDepositAddress(row), coboAddress)) return false;
    if (!isCoboOrderInProgressOrder(row)) return false;
    if (floor > 0) {
      const created = orderCreatedMs(row);
      if (created && created < floor) return false;
    } else {
      return false;
    }
    const tx = normalizeTxHashKey(extractCoboDepositTxid(row));
    const id = extractCoboOrderId(row);
    if (tx && knownTx.has(tx)) return false;
    if (id && knownIds.has(id)) return false;
    return true;
  });
}

/** 扫码前已存在的充值 tx，写入 seen 以免链上回看再弹状态 */
export function collectHistoricalCoboDepositTxids(
  orders,
  coboAddressOrList,
  { notBeforeMs = 0 } = {}
) {
  const list = Array.isArray(orders) ? orders : [];
  const floor = Number(notBeforeMs) > 0 ? Number(notBeforeMs) : 0;
  const allowed = normalizeCoboAddressList(coboAddressOrList);
  const hashes = [];
  for (const row of list) {
    if (!coboAddressAllowed(row, allowed)) continue;
    const created = orderCreatedMs(row);
    // 必须有明确创建时间且早于会话，才写入 seen；无时间戳绝不当历史（曾导致新单中间态被吞）
    if (!created || !floor || created >= floor) continue;
    const tx = extractCoboDepositTxid(row);
    if (tx) hashes.push(tx);
  }
  return hashes;
}

/** 扫码前已存在的订单 id，避免首见已是 status=5 时把历史完成单弹出来 */
export function collectHistoricalCoboOrderIds(orders, coboAddressOrList, { notBeforeMs = 0 } = {}) {
  const list = Array.isArray(orders) ? orders : [];
  const floor = Number(notBeforeMs) > 0 ? Number(notBeforeMs) : 0;
  const allowed = normalizeCoboAddressList(coboAddressOrList);
  const ids = [];
  for (const row of list) {
    if (allowed.length && !coboAddressAllowed(row, allowed)) {
      const hasAnyAddress = list.some((item) => extractCoboDepositAddress(item));
      if (hasAnyAddress) continue;
    }
    const created = orderCreatedMs(row);
    if (!created || !floor || created >= floor) continue;
    const id = extractCoboOrderId(row);
    if (id) ids.push(id);
  }
  return ids;
}

/**
 * 本会话内尚未展示的终态单（完成 / 失败）。
 * 用于无链上扫时：订单从无到 status=5 也能出确认弹窗。
 * coboAddressOrList：多链任一收款地址命中即可。
 */
export function pickNewestUnseenTerminalCoboOrder(
  orders,
  coboAddressOrList,
  { notBeforeMs = 0, seenOrderIds = [] } = {}
) {
  const list = Array.isArray(orders) ? orders : [];
  const floor = Number(notBeforeMs) > 0 ? Number(notBeforeMs) : 0;
  if (!floor) return null;
  const allowed = normalizeCoboAddressList(coboAddressOrList);
  const seen = new Set(
    (Array.isArray(seenOrderIds) ? seenOrderIds : [])
      .map((id) => String(id ?? '').trim())
      .filter(Boolean)
  );

  function isTerminal(row) {
    const status = resolveCoboOrderStatus(row);
    return isCoboOrderCompleted(status) || isCoboOrderFailed(status);
  }

  function inSession(row) {
    if (!isTerminal(row)) return false;
    const id = extractCoboOrderId(row);
    if (id && seen.has(id)) return false;
    const created = orderCreatedMs(row);
    if (created && created < floor) return false;
    // 无创建时间：用 updatedAt 兜底；仍无则保留（避免漏首包已完成单）
    if (!created) {
      const updated = coerceTimeMs(row?.updatedAt) || coerceTimeMs(row?.updateAt);
      if (updated && updated < floor) return false;
    }
    return true;
  }

  let candidates = list.filter((row) => coboAddressAllowed(row, allowed) && inSession(row));
  if (!candidates.length) {
    const hasAnyAddress = list.some((row) => extractCoboDepositAddress(row));
    if (!hasAnyAddress) {
      candidates = list.filter((row) => inSession(row));
    }
  }
  if (!candidates.length) return null;
  candidates.sort((a, b) => {
    const dt = orderCreatedMs(b) - orderCreatedMs(a);
    if (dt !== 0) return dt;
    return String(extractCoboOrderId(b)).localeCompare(String(extractCoboOrderId(a)));
  });
  return candidates[0];
}
